import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import ReactModal from "react-modal";
import { COUNTRY, COUNTRY_OPTION, EXCHANGE_RATE_TYPE } from "../../constant";

const defaultFormValue = {
    country_code: "",
    currency_unit: "",
    label: null,
    type: EXCHANGE_RATE_TYPE.SPECIAL_TRANSACTION,
    rate: null,
    min_trans_amount: null,
    enable: "false"
}

const countryOptions = [
    { value: "", label: "Please select the country" },
    ...COUNTRY_OPTION
];

const isEnableOptions = [
    { value: "true", label: "Enable" },
    { value: "false", label: "Disable" },
]

export const COUNTRY_MAP = new Map(COUNTRY.map(c => [c.code, c]));

const ExchangeRateModal = ({
    isOpen,
    onOk,
    onCancel,
    header,
    loading = false,
    width = undefined,
    userGroup,
    mode,
    data,
}) => {
    const {
        reset,
        register,
        control,
        handleSubmit,
        watch,
        formState: { errors },
        setValue,
    } = useForm({
        mode: "onChange",
        defaultValues: defaultFormValue
    });

    const [selectedType, setSelectedType] = useState("");
    const watchedType = watch("type");
    const watchedCountry = watch("country_code");
    const fxRole = userGroup?.includes("/services/irs/irs-exchange-rate");
    const adminRole = userGroup?.includes("/services/irs/irs-administrator");

    useEffect(() => {
        // if open in edit mode then set value to form
        if (data && isOpen) {
            setValue("country_code", data.country_code);
            setValue("currency_unit", data.currency_unit);
            setValue("label", data.label);
            setValue("type", data.type);
            setValue("rate", data.rate);
            setValue("min_trans_amount", data.min_trans_amount);
            setValue("enable", data.is_disable ? "false" : "true");
        } else {
            reset();
        }
    }, [data, isOpen]);

    // on type change
    useEffect(() => {
        setSelectedType(watchedType);
        switch (watchedType) {
            case EXCHANGE_RATE_TYPE.NEW_CUSTOMER:
                setValue("min_trans_amount");
                break;
            case EXCHANGE_RATE_TYPE.SPECIAL_TRANSACTION:
                setValue("min_trans_amount", data?.min_trans_amount);
                break;
        }
    }, [watchedType]);

    // on country code change
    useEffect(() => {
        if (watchedCountry) {
            setValue("currency_unit", COUNTRY_MAP.get(watchedCountry)?.currency);
        } else {
            setValue("currency_unit", "");
        }
    }, [watchedCountry])

    const validateName = (value) => {
        if (!fxRole) {
            const trimmedValue = value.replace(/(?:^\s+)|(?:\s+$)/g, '').replace(/\s+/g, ' ');
            if (!/^[a-zA-Z0-9 ]+$/.test(trimmedValue)) {
                return "Name must contain only English alphabets, digits, and spaces";
            }
            setValue('name', trimmedValue);
            return true;
        }
    };

    const validateRate = (value) => {
        if (!adminRole) {
            if (parseFloat(value) <= 0) {
                return "Rate must be greater than zero";
            }
            if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                return "Rate must be a number with up to 2 decimal places";
            }
            return true;
        }
    };

    const validateMinTransAmount = (value) => {
        if (adminRole && selectedType === EXCHANGE_RATE_TYPE.SPECIAL_TRANSACTION) {
            if (!value) {
                return "Minimum transaction amount is required for Special type";
            }
            if (!/^\d+$/.test(value)) {
                return "Minimum transaction amount must be a whole number";
            }
            const amount = parseInt(value, 10);
            if (amount <= 0 || amount > 30000) {
                return "Minimum transaction amount must be between 1 and 30,000 THB";
            }
            return true;
        }
    };

    function onChangeMinTransAmount(e) {
        const value = e.target.value.replace(/\D/g, "");
        e.target.value = value;
    }

    function mapOptions(options) {
        return options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
        ));
    }

    const onSubmit = (formValue) => {
        const updatedExchangeRate = {
            id: data?.exchange_rate_id,
            country_code: formValue.country_code,
            currency_unit: formValue.currency_unit,
            name: formValue.label,
            rate: formValue.rate,
            min_trans_amount: formValue.min_trans_amount,
            type: formValue.type.toUpperCase().replace(" ", "_"),
            is_disable: formValue.enable === "true" ? false : true
        };
        onOk(updatedExchangeRate);
    };

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={onCancel}
            ariaHideApp={false}
            className="custom-modal"
            style={{
                overlay: {
                    zIndex: "5000",
                },
            }}
        >
            <div className="exchange-rate-modal" style={{ 'width': width || 'unset' }}>
                <div className="custom-modal-content">
                    <h5 className="custom-modal-message text-center p-3">{header}</h5>
                    <div className="custom-modal-description">
                        <form id="exchangeRateForm" onSubmit={handleSubmit(onSubmit)}>
                            <div className="form-group row">
                                <label htmlFor="country" className="col-sm-5 col-form-label">Country </label>
                                <div className="col-sm-7">
                                    <select
                                        className="form-control"
                                        id="country"
                                        disabled={fxRole}
                                        {...register("country_code", {
                                            required: countryOptions[0].label
                                        })}
                                    >
                                        {mapOptions(countryOptions)}
                                    </select>
                                    {errors.country_code && <div className="text-danger text-left mt-1">{errors.country_code.message}</div>}
                                </div>
                            </div>
                            <div className="form-group row">
                                <label htmlFor="currency" className="col-sm-5 col-form-label">Currency </label>
                                <div className="col-sm-7">
                                    <input
                                        id="currency"
                                        type="text"
                                        readOnly
                                        className="form-control"
                                        disabled
                                        {...register("currency_unit", {
                                            required: countryOptions[0].label
                                        })}
                                    />
                                </div>
                            </div>
                            <div className="form-group row">
                                <label htmlFor="name" className="col-sm-5 col-form-label">Name </label>
                                <div className="col-sm-7">
                                    <input
                                        type="text"
                                        className="form-control"
                                        disabled={fxRole}
                                        data-testid="name-input"
                                        id="name"
                                        {...register("label", {
                                            required: {
                                                value: !fxRole,
                                                message: "Name is required"
                                            },
                                            validate: validateName,
                                        })}
                                    />
                                    {errors.label &&
                                        <div className="text-danger text-left mt-1">{errors.label.message}</div>}
                                </div>
                            </div>
                            <div className="form-group row">
                                <label htmlFor="type" className="col-sm-5 col-form-label">Type </label>
                                <div className="col-sm-7">
                                    <Controller
                                        name="type"
                                        control={control}
                                        rules={{
                                            required: {
                                                value: mode === "ADD" && !fxRole,
                                                message: "Type is required"
                                            }
                                        }}
                                        render={({ field }) => (
                                            <div className="text-left">
                                                {["Special Customer","Special Transaction", "New Customer"].map((option) => (
                                                    <div key={option} className="form-check-inline">
                                                        <input
                                                            className="form-check-input"
                                                            type="radio"
                                                            disabled={mode !== "ADD" && fxRole}
                                                            id={`type${option.replace(/\s+/g, '')}`}
                                                            value={option}
                                                            {...field}
                                                            checked={field.value === option}
                                                            onChange={() => field.onChange(option)}
                                                        />
                                                        <label className="form-check-label"
                                                            htmlFor={`type${option.replace(/\s+/g, '')}`}>
                                                            {option}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    />
                                    {errors.type &&
                                        <div className="text-danger text-left mt-1">{errors.type.message}</div>}
                                </div>
                            </div>
                            <div className="form-group row">
                                <label htmlFor="rate" className="col-sm-5 col-form-label">Rate </label>
                                <div className="col-sm-7">
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="rate"
                                        disabled={adminRole}
                                        {...register("rate", {
                                            required: {
                                                value: !adminRole,
                                                message: "Rate is required"
                                            },
                                            validate: validateRate
                                        })}
                                    />
                                    {errors.rate &&
                                        <div className="text-danger text-left mt-1">{errors.rate.message}</div>}
                                </div>
                            </div>
                            <div className="form-group row">
                                <label htmlFor="min_trans_amount" className="col-sm-5 col-form-label">
                                    Minimum Transaction Amount
                                </label>
                                <div className="col-sm-7">
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="min_trans_amount"
                                        disabled={fxRole || selectedType === EXCHANGE_RATE_TYPE.NEW_CUSTOMER}
                                        {...register("min_trans_amount", {
                                            validate: validateMinTransAmount,
                                            onChange: onChangeMinTransAmount,
                                        })}
                                    />
                                    {errors.min_trans_amount && <div
                                        className="text-danger text-left mt-1">{errors.min_trans_amount.message}</div>}
                                </div>
                            </div>
                            <div className="form-group row">
                                <label htmlFor="enable" className="col-sm-5 col-form-label">Enable </label>
                                <div className="col-sm-7">
                                    <div className="text-left">
                                        {isEnableOptions.map(option => (
                                            <div key={option.value} className="form-check-inline">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    disabled={fxRole || mode === "ADD"}
                                                    value={option.value}
                                                    id={`is-enable-radio-${option.value}`}
                                                    {...register("enable")}
                                                />
                                                <label className="form-check-label" htmlFor={`is-enable-radio-${option.value}`}>
                                                    {option.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                    {errors.enable && <div
                                        className="text-danger text-left mt-1">{errors.enable.message}</div>}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                <div className="custom-modal-action">
                    <button
                        data-testid="exchange-rate-modal-cancel-btn"
                        className="btn btn-secondary"
                        type="button"
                        onClick={onCancel}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="exchange-rate-modal-submit-btn"
                        className="btn btn-warning"
                        type="submit"
                        form="exchangeRateForm"
                        disabled={loading}
                    >
                        Submit
                    </button>
                </div>
            </div>
        </ReactModal>
    );
};

export default ExchangeRateModal;